import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface KpiCardProps {
  label: string;
  value: string;
  subValue?: string;
  change?: number;
  changeLabel?: string;
  highlight?: boolean;
  alert?: boolean;
  mono?: boolean;
  icon?: React.ReactNode;
  colSpan?: 1 | 2;
}

export default function KpiCard({
  label,
  value,
  subValue,
  change,
  changeLabel,
  highlight,
  alert,
  mono,
  icon,
  colSpan = 1,
}: KpiCardProps) {
  const isPositive = change !== undefined && change > 0;
  const isNegative = change !== undefined && change < 0;
  const isNeutral = change !== undefined && change === 0;

  const cardClass = highlight
    ? 'rounded-xl border p-5 bg-btcgold-500/5 border-btcgold-500/30 glow-primary-sm'
    : alert
    ? 'rounded-xl border p-5 bg-red-500/5 border-red-500/25' :'rounded-xl border p-5 bg-card border-zinc-800';

  const colClass = colSpan === 2 ? 'col-span-1 md:col-span-2' : 'col-span-1';

  return (
    <div className={`${cardClass} ${colClass} flex flex-col gap-3`}>
      <div className="flex items-center justify-between">
        <span
          className="text-xs font-medium tracking-wide uppercase"
          style={{ color: 'hsl(0 0% 55%)', letterSpacing: '0.06em' }}
        >
          {label}
        </span>
        {icon && <span className="text-zinc-500">{icon}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <span
          className={`font-bold tabular-nums leading-none ${
            mono ? 'font-mono text-xl' : 'text-2xl lg:text-3xl'
          } ${highlight ? 'text-btcgold-400' : alert ? 'text-red-400' : 'text-white'}`}
        >
          {value}
        </span>
        {subValue && (
          <span className="text-xs text-zinc-500 font-mono">{subValue}</span>
        )}
      </div>

      {change !== undefined && (
        <div
          className={`flex items-center gap-1 text-xs font-medium ${
            isPositive ? 'text-green-400' : isNegative ? 'text-red-400' : 'text-zinc-500'
          }`}
        >
          {isPositive && <TrendingUp size={12} />}
          {isNegative && <TrendingDown size={12} />}
          {isNeutral && <Minus size={12} />}
          <span>
            {isPositive ? '+' : ''}
            {change?.toFixed(2)}%
          </span>
          {changeLabel && <span className="text-zinc-500 font-normal">{changeLabel}</span>}
        </div>
      )}
    </div>
  );
}